import React, { useState } from 'react';
import {
  DeviceMobile,
  XCircle,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import {
  trackUpdateProjectSuccess,
  trackUpdateProjectError,
} from '../../../../analytics';

import { Form } from '../../../../components/form';
import { Button } from '../../../../components/general';

import { updateEvent } from '../../../../services/api/events';

import { useProjectStore } from '../../../../stores/project';

import { Event } from '../../../../types';
import { isEmpty, scrollToTop } from '../../../../utils';

import HeadingWithInfo from './headingWithInfo';
import { validateForm } from './projectSettingsValidations';

interface TabBlock {
  isHide?: boolean;
  title: string;
  description?: string | React.ReactNode;
  infoHeader?: string;
  infoBody?: string | React.ReactNode;
  content: React.ReactNode;
}

interface TabPreview {
  previewRef: React.RefObject<HTMLDivElement>;
  show: boolean;
  onShow: () => void;
  onHide: () => void;
  onClickLeft?: () => void;
  onClickRight?: () => void;
  content: React.ReactNode;
  note?: string;
}

interface Props {
  title?: string;
  subtitle?: string | React.ReactNode;
  event: Event;
  data: any;
  validationType?: string;
  setErrors?: (errors: Record<string, string>) => void;
  blocks: TabBlock[];
  preview?: TabPreview;
}

const TabComponent: React.FC<Props> = (props) => {
  const {
    title,
    subtitle,
    event,
    data,
    validationType,
    setErrors,
    blocks,
    preview,
  } = props;

  const { updateCurrentEvent } = useProjectStore();

  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();

    setIsSaving(true);

    if (!!validationType && !!setErrors) {
      let errors: Record<string, string> = {};

      errors = await validateForm(validationType, data);

      if (!isEmpty(errors)) {
        setErrors(errors);
        scrollToTop();
        toast.error('Some data is missing or invalid');
        setIsSaving(false);
        return;
      }
    }

    // if no errors – save data
    try {
      await updateEvent(event.eventId, data);
      await updateCurrentEvent();
      trackUpdateProjectSuccess();
      toast.success('Project is updated');
    } catch {
      trackUpdateProjectError();
      toast.error('Something went wrong. Try again later');
    } finally {
      setIsSaving(false);
    }

    // session storage cleanup
    sessionStorage.removeItem('attendees');
  };

  const previewContainerClass = classNames({
    'w-[75%] aspect-[1/2]': true,
    'max-h-[var(--mobile-preview-height)]': !preview?.note,
    'max-h-[var(--mobile-preview-height-with-note)]': preview?.note,
  });

  const previewContentClass = classNames({
    'absolute top-[calc(1.125rem_*_1.5_+_1rem)] left-0 w-full aspect-[1/2] scale-75 origin-top rounded-3xl shadow-grey-2 overflow-x-hidden overflow-y-scroll': true,
    'max-h-[calc(var(--mobile-preview-height)_/_0.75)]': !preview?.note,
    'max-h-[calc(var(--mobile-preview-height-with-note)_/_0.75)]':
      preview?.note,
  });

  const caretBtnClass =
    'absolute top-[50%] w-8 aspect-square flex items-center justify-center p-2 bg-black text-white opacity-20 hover:opacity-40 rounded-full cursor-pointer';

  return (
    <div className="flex gap-10">
      <div
        className={classNames(
          'w-full flex flex-col gap-6 sm:gap-8 transition-all',
          { 'lg:w-[58%]': preview?.show },
        )}
      >
        {(title || subtitle) && (
          <div className="flex flex-col gap-2">
            {title && (
              <div className="flex items-center justify-between">
                <h3 className="font-semibold leading-[2.5rem]">{title}</h3>

                {preview && !preview.show && (
                  <Button
                    onClick={preview.onShow}
                    variant="primary-thin"
                    colour="black-outline"
                    className="hidden lg:flex text-[1.25rem]"
                    withGap
                  >
                    <DeviceMobile />
                    <div className="text-sm font-bold">Preview</div>
                  </Button>
                )}
              </div>
            )}

            {subtitle && <div>{subtitle}</div>}
          </div>
        )}

        <Form onSubmit={onSubmit} disabled={isSaving} className="gap-8">
          {blocks.map(
            (block) =>
              !block.isHide && (
                <div
                  key={block.title}
                  className="flex flex-col p-4 gap-4 rounded-md border border-grey-200 border-solid"
                >
                  <div className="flex flex-col gap-2">
                    <HeadingWithInfo
                      subheading={block.title}
                      infoHeader={block.infoHeader}
                      infoBody={block.infoBody}
                    />

                    {block.description && (
                      <div className="text-sm text-black/[0.5]">
                        {block.description}
                      </div>
                    )}
                  </div>

                  {block.content}
                </div>
              ),
          )}

          <Button type="submit" isSubmitBtn isProcessing={isSaving}>
            Save
          </Button>
        </Form>
      </div>

      {preview?.show && (
        <div className="hidden lg:block w-[42%]">
          <div className="sticky top-0 w-full flex flex-col items-center gap-4">
            <div className="relative w-[75%] flex items-center justify-between">
              <div className="text-lg font-bold">Preview</div>

              <div
                className="group w-6 h-6 text-xl leading-none cursor-pointer"
                onClick={preview.onHide}
              >
                <XCircle className="absolute opacity-100 group-hover:opacity-0" />
                <XCircle
                  weight="fill"
                  className="absolute opacity-0 group-hover:opacity-100"
                />
              </div>
            </div>

            <div className={previewContainerClass}>
              {preview.onClickLeft && (
                <div
                  className={classNames(caretBtnClass, 'left-[2%]')}
                  onClick={preview.onClickLeft}
                >
                  <CaretLeft weight="bold" />
                </div>
              )}

              <div ref={preview.previewRef} className={previewContentClass}>
                {preview.content}

                <div id="preview-modal-root" />
              </div>

              {preview.onClickRight && (
                <div
                  className={classNames(caretBtnClass, 'right-[2%]')}
                  onClick={preview.onClickRight}
                >
                  <CaretRight weight="bold" />
                </div>
              )}
            </div>

            {preview.note && (
              <div className="text-sm italic text-center">{preview.note}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TabComponent;
